import { PAGE_REGISTRY, type PageRoute } from '@/site/pageRegistry';

// PrerenderRoute re-exported from routes.ts for callers that use it;
// internally we use PageRoute directly.
type PrerenderRoute = PageRoute;

/**
 * Internal navigation links for all prerendered routes except the homepage. `href` is the path of
 * the page's canonical URL (always slashed): the slashless route 301s to it on Firebase Hosting.
 */
export const FOOTER_NAV_LINKS: ReadonlyArray<{ route: Exclude<PrerenderRoute, '/'>; href: string; label: string }> =
    PAGE_REGISTRY
        .filter((p): p is (typeof PAGE_REGISTRY)[number] & { route: Exclude<PageRoute, '/'> } => p.route !== '/')
        .map((p) => ({ route: p.route, href: new URL(p.canonical).pathname, label: p.label }));

// Named canonical URL constants -- kept for backward compat (crawler.test.ts, page imports).
export const ROOT_CANONICAL_URL = PAGE_REGISTRY.find((p) => p.route === '/')!.canonical;
export const BLUEBEAM_FOR_MAC_CANONICAL_URL = PAGE_REGISTRY.find((p) => p.route === '/bluebeam-for-mac')!.canonical;
export const BLUEBEAM_REVU_FREE_ALTERNATIVE_CANONICAL_URL = PAGE_REGISTRY.find(
    (p) => p.route === '/bluebeam-revu-free-alternative',
)!.canonical;
export const MEASURE_PDF_ONLINE_CANONICAL_URL = PAGE_REGISTRY.find(
    (p) => p.route === '/measure-pdf-online',
)!.canonical;
export const DRAWING_SCALES_CANONICAL_URL = PAGE_REGISTRY.find(
    (p) => p.route === '/drawing-scales',
)!.canonical;
export const REDLINE_PDF_DRAWING_CANONICAL_URL = PAGE_REGISTRY.find(
    (p) => p.route === '/redline-pdf-drawing',
)!.canonical;
export const WHATS_NEW_CANONICAL_URL = PAGE_REGISTRY.find(
    (p) => p.route === '/whats-new',
)!.canonical;
export const DE_CANONICAL_URL = PAGE_REGISTRY.find((p) => p.route === '/de')!.canonical;
export const FR_CANONICAL_URL = PAGE_REGISTRY.find((p) => p.route === '/fr')!.canonical;

export function canonicalUrlForRoute(route: PrerenderRoute): string {
    const entry = PAGE_REGISTRY.find((p) => p.route === route);
    if (!entry) throw new Error(`No canonical URL for route: ${route}`);
    return entry.canonical;
}

export function buildSitemapXml(): string {
    const lines = [
        '<?xml version="1.0" encoding="UTF-8"?>',
        '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
        ...PAGE_REGISTRY.map((entry) =>
            `  <url><loc>${entry.canonical}</loc><lastmod>${entry.reviewed}</lastmod><changefreq>${entry.changefreq}</changefreq><priority>${entry.priority}</priority></url>`,
        ),
        '</urlset>',
        '',
    ];
    return lines.join('\n');
}

export function buildLlmsTxt(): string {
    return [
        '# PDF Markup Tool',
        '',
        'PDF markup software for architects and engineers.',
        'Redline drawings, take measurements, and export finished PDFs locally.',
        'Free in any browser. No install required.',
        '',
        '## Capabilities',
        '- Line, polyline, polygon, rectangle, cloud, callout, arrow, highlight, and text markups on any PDF drawing',
        '- Length, area, and perimeter measurement tools calibrated to drawing scale',
        '- Scale calibration from architectural ratio or by measuring a known distance',
        '- Annotated PDF export with markups flattened into the stamped drawing',
        '- CSV markup data export for QA and downstream estimating workflows',
        '- Nothing uploaded: PDFs open and auto-save in browser storage on your device',
        '',
        '## Technology',
        'Browser-based React application. No install, no Windows requirement.',
        'Compatible with Chrome, Safari, Firefox, and Edge.',
        '',
        '## Pages',
        ...PAGE_REGISTRY.map((entry) => `- [${entry.label}](${entry.canonical})`),
        '',
        '> URL: https://pdfmarkuptool.com/',
        '',
    ].join('\n');
}
